import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';
import worldGeoJson from '../utils/customGeoJson.json';
import './css/EarthGlobe.css';

function Visualization1Globe() {
  const [selectedCountries, setSelectedCountries] = useState([]); // Array to handle multiple selections
  const [countryData, setCountryData] = useState([]);
  const [allData, setAllData] = useState([]);
  const [tooltipContent, setTooltipContent] = useState(""); // Tooltip content
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  useEffect(() => {
    d3.csv(`${process.env.PUBLIC_URL}/data/international_tourist_trips_final.csv`).then((loadedData) => {
      const parsedData = loadedData.map(d => ({
        country: d.Entity,
        code: d.Code,
        year: +d.Year,
        trips: +d['Inbound arrivals of tourists']
      }));
      setAllData(parsedData);
      const yearData = parsedData.filter(d => d.year === 2022);
      const cleanup = drawGlobe(yearData);
      drawLegend();

      return cleanup; // This will stop the rotation when the component unmounts
    });
  }, [selectedCountries, isAutoRotating]);

  const drawGlobe = (yearData) => {
    // Clear previous globe
    d3.select("#globe").selectAll("*").remove();

    const width = 800;
    const height = 600;

    const svg = d3.select("#globe")
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const projection = d3.geoOrthographic()
      .scale(250)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Define a more detailed color scale using a broader range of colors
    const colorScale = d3.scaleThreshold()
      .domain([5000, 50000, 100000, 500000, 1000000, 10000000, 20000000])
      .range(d3.schemeBlues[8]);  // Using a color scheme with 8 steps

    // Use a single tooltip element
    const tooltip = d3.select("body").append("div")
      .attr("class", "tooltip")
      .style("position", "absolute")
      .style("opacity", 0)
      .style("background", "white")
      .style("border", "1px solid black")
      .style("padding", "5px")
      .style("pointer-events", "none");

    // Draw countries on the globe
    svg.append("g")
      .selectAll("path")
      .data(worldGeoJson.features)
      .enter()
      .append("path")
      .attr("d", path)
      .attr("fill", d => {
        const countryData = yearData.find(country => country.code === d.properties.adm0_a3);
        return countryData ? colorScale(countryData.trips) : "#ccc";
      })
      .attr("stroke", d => selectedCountries.includes(d.properties.adm0_a3) ? "#ff0000" : "#2e2c2c") // Highlight selected countries
      .attr("stroke-width", d => selectedCountries.includes(d.properties.adm0_a3) ? 2 : 0.5)
      .on("mouseover", (event, d) => {
        const countryData = yearData.find(country => country.code === d.properties.adm0_a3);
        const tourists = countryData ? countryData.trips : 0; // Default to 0 tourists
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip.html(`
        <strong>${d.properties.name}</strong><br>
        Tourists: ${tourists ? tourists.toLocaleString() : "No data available"}
      `)
          .style("left", `${event.pageX + 10}px`)
          .style("top", `${event.pageY + 10}px`);
      })
      .on("mousemove", (event) => {
        tooltip.style("left", `${event.pageX + 10}px`).style("top", `${event.pageY + 10}px`);
      })
      .on("mouseout", () => {
        tooltip.transition().duration(200).style("opacity", 0);
      })
      .on("click", (event, d) => {
        const countryCode = d.properties.adm0_a3;
        const isSelected = selectedCountries.includes(countryCode);

        // Toggle country selection
        const updatedSelection = isSelected
          ? selectedCountries.filter(code => code !== countryCode) // Remove if already selected
          : [...selectedCountries, countryCode]; // Add if not selected

        setSelectedCountries(updatedSelection);

        // Explicitly hide the tooltip on click
        tooltip.transition().duration(200).style("opacity", 0);
      });

    let rotationTimer;

    const startRotation = () => {
      const rotate = projection.rotate();
      rotationTimer = d3.timer((elapsed) => {
        const rotation = [(rotate[0] - elapsed * 0.01) % 360, rotate[1], rotate[2]];
        projection.rotate(rotation);
        svg.selectAll("path").attr("d", path);
      });
    };

    const stopRotation = () => {
      if (rotationTimer) rotationTimer.stop();
    };

    if (isAutoRotating) {
      startRotation();
    }

    // Drag functionality for rotating the globe
    const drag = d3.drag()
      .on("start", () => {
        stopRotation();
        setIsAutoRotating(false);
      })
      .on("drag", (event) => {
        const rotate = projection.rotate();
        const newRotate = [rotate[0] + event.dx / 5, rotate[1] - event.dy / 5];
        projection.rotate(newRotate);
        svg.selectAll("path").attr("d", path);
      });

    svg.call(drag);
    return () => {
      stopRotation();
    };
  };

  const drawLegend = () => {
    // Clear the existing legend if any
    d3.select("#legend").selectAll("*").remove();
  
    const legendWidth = 400; // Adjusted width for more color steps
    const legendHeight = 20; // Height of the color boxes
    const svg = d3.select("#legend")
      .append("svg")
      .attr("width", legendWidth)
      .attr("height", legendHeight + 70); // Increased space for rotated labels
  
    // Expanded color scale with more gradations
    const colorScale = d3.scaleThreshold()
      .domain([1000, 10000, 50000, 100000, 500000, 1000000, 10000000, 20000000]) // Expanded range
      .range(d3.schemeBlues[8]); // 8 colors for better granularity
  
    // Legend data based on the domain of the color scale
    const legendData = [1000, 10000, 50000, 100000, 500000, 1000000, 10000000, 20000000];
  
    // Function to truncate numbers to readable labels
    const formatLabel = (value) => {
      if (value >= 1000000) {
        return (value / 1000000) + 'M'; // e.g., 1M
      } else if (value >= 1000) {
        return (value / 1000) + 'k'; // e.g., 1k
      } else {
        return value; // Return as is for small values
      }
    };
  
    // Add "No Data" legend item first
    svg.append("rect")
      .attr("x", 0)
      .attr("y", 10)
      .attr("width", legendWidth / (legendData.length + 1))
      .attr("height", legendHeight)
      .attr("fill", "#ccc"); // Gray color for no data
  
    svg.append("text")
      .attr("x", (legendWidth / (legendData.length + 1)) / 2)
      .attr("y", 45)
      .attr("text-anchor", "middle")
      .attr("font-size", "10px")
      .text("No Data");
  
    // Create color boxes for the rest of the legend
    legendData.forEach((d, i) => {
      svg.append("rect")
        .attr("x", (i + 1) * (legendWidth / (legendData.length + 1))) // Adjusted spacing to account for "No Data"
        .attr("y", 10)
        .attr("width", legendWidth / (legendData.length + 1))
        .attr("height", legendHeight)
        .attr("fill", colorScale(d));
  
      // Add labels below each color box, rotated
      svg.append("text")
        .attr("x", (i + 1) * (legendWidth / (legendData.length + 1)) + (legendWidth / (legendData.length + 1)) / 2)
        .attr("y", 45)
        .attr("text-anchor", "middle")
        .attr("font-size", "10px")
        .text(formatLabel(d));
    });
  
     // Add title to the legend
     svg.append("text")
       .attr("x", legendWidth / 2)
       .attr("y", legendHeight + 60) // Adjusted position for the title to avoid overlap
       .attr("text-anchor", "middle")
       .attr("font-size", "12px")
       .text("Tourists (2022)");
  };

  // Draw the line chart for selected countries
  useEffect(() => {
    if (selectedCountries.length === 0) return;
    const countriesData = allData.filter(d => selectedCountries.includes(d.code));
    setCountryData(countriesData);
    drawLineChart(countriesData);
  }, [selectedCountries]);

  const drawLineChart = (countriesData) => {
    d3.select("#line-chart").selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 50, left: 60 };
    const width = 500 - margin.left - margin.right;
    const height = 450 - margin.top - margin.bottom;

    const svg = d3.select("#line-chart")
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom)
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const xScale = d3.scaleLinear()
      .domain(d3.extent(countriesData, d => d.year))
      .range([0, width]);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(countriesData, d => d.trips)])
      .range([height, 0]);

    const line = d3.line()
      .x(d => xScale(d.year))
      .y(d => yScale(d.trips));

    // Draw X and Y axes
    svg.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.format("d")));

    svg.append("g")
      .call(d3.axisLeft(yScale).tickSize(6));

    const colorScale = d3.scaleOrdinal(d3.schemeTableau10);

    // Draw lines and points for each country
    selectedCountries.forEach((countryCode, i) => {
      const countryData = countriesData.filter(d => d.code === countryCode);

      svg.append("path")
        .datum(countryData)
        .attr("fill", "none")
        .attr("stroke", colorScale(i))
        .attr("stroke-width", 2)
        .attr("d", line);

      svg.selectAll(`circle-${countryCode}`)
        .data(countryData)
        .enter()
        .append("circle")
        .attr("cx", d => xScale(d.year))
        .attr("cy", d => yScale(d.trips))
        .attr("r", 4)
        .attr("fill", colorScale(i));
    });

    // Tooltip container
    const tooltip = d3.select("body").append("div")
      .attr("class", "tooltip-content")
      .style("position", "absolute")
      .style("opacity", 0)
      .style("background", "white")
      .style("border", "1px solid #ccc")
      .style("padding", "8px")
      .style("border-radius", "5px")
      .style("box-shadow", "0px 2px 6px rgba(0, 0, 0, 0.1)");

    // Vertical line for hover
    const verticalLine = svg.append("line")
      .attr("class", "vertical-line")
      .attr("stroke", "black")
      .attr("stroke-width", 1)
      .style("opacity", 0);

    svg.on("mousemove", function (event) {
      const mouseX = d3.pointer(event)[0];
      const yearValue = Math.round(xScale.invert(mouseX));
      const closestData = countriesData.filter(d => d.year === yearValue);

      if (closestData.length > 0) {
        const xPosition = xScale(yearValue);

        verticalLine
          .attr("x1", xPosition)
          .attr("x2", xPosition)
          .attr("y1", 0)
          .attr("y2", height)
          .style("opacity", 1);

        tooltip
          .style("opacity", 1)
          .style("left", `${event.pageX + 15}px`)
          .style("top", `${event.pageY + 15}px`)
          .html(
            closestData
              .map(
                (d, i) =>
                  `<span style="color:${colorScale(
                    selectedCountries.indexOf(d.code)
                  )}; font-weight: bold;">${d.country}:</span> ${d.trips.toLocaleString()}`
              )
              .join("<br>")
          );
      }
    });

    svg.on("mouseout", () => {
      verticalLine.style("opacity", 0);
      tooltip.style("opacity", 0);
    });
  };



  return (
    <div>
      <div className="header-container">
        <h2>
          International Tourist Trips - 2022
        </h2>
        <p>
          Trips by people who arrive from abroad and stay overnight.
        </p>
      </div>
      <div className="visualization-container-earth">
        <div className="slider-container">
          {/* <h1 className="viz-heading">3D Globe of International Tourist Trips (2022):</h1> */}
          <div id="globe"></div>
          <div id="legend"></div>
        </div>
        <div className="side-panel">
          <div id="line-chart" className="line-chart"></div>
          {tooltipContent && (
            <div className="tooltip-content">
              {tooltipContent}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Visualization1Globe;

